import type { Difficulty, MCQQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/** Format a decimal to one decimal place, removing unnecessary trailing zeros */
function fmt(n: number): string {
  return (Math.round(n * 10) / 10).toFixed(1);
}

type OpType = 'add' | 'sub' | 'compare';

/**
 * N-3-10：一位小數的加減計算與比較。
 * difficulty 1：0.x + 0.x（結果 < 2，無進位到個位）
 * difficulty 2：x.y + x.y 或 x.y − x.y（有進位/借位可能）
 * difficulty 3：多步驟比較或加減，結果可達 9.9
 */
export function generateDecAdd(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  const opChoices: OpType[] = difficulty === 1
    ? ['add', 'compare']
    : difficulty === 2
    ? ['add', 'sub', 'compare']
    : ['add', 'sub', 'compare'];

  const op = opChoices[randInt(rand, 0, opChoices.length - 1)];

  let prompt: string;
  let correct: string;
  let options: string[];
  let explanation: string;

  if (op === 'add') {
    let a: number, b: number;
    if (difficulty === 1) {
      a = randInt(rand, 1, 9) / 10; // 0.1 – 0.9
      b = randInt(rand, 1, 9) / 10;
    } else if (difficulty === 2) {
      a = randInt(rand, 10, 59) / 10; // 1.0 – 5.9
      b = randInt(rand, 10, 49) / 10;
    } else {
      a = randInt(rand, 10, 89) / 10;
      b = randInt(rand, 10, 89) / 10;
    }
    const sum = Math.round((a + b) * 10) / 10;
    correct = fmt(sum);
    prompt = `${fmt(a)} + ${fmt(b)} = ?`;

    const w1 = fmt(sum + 0.1);
    const w2 = fmt(sum - 0.1 >= 0 ? sum - 0.1 : sum + 0.2);
    const w3 = fmt(Math.round((a + b) * 10 + (rand() > 0.5 ? 1 : -1)) / 10 === sum
      ? sum + 1.0 : Math.round((a + b) * 10 + (rand() > 0.5 ? 1 : -1)) / 10);

    options = [correct, w1, w2, w3];
    explanation = `小數加法：個位和小數位分別相加。${fmt(a)} + ${fmt(b)} = ${correct}`;

  } else if (op === 'sub') {
    let a: number, b: number;
    if (difficulty === 1) {
      b = randInt(rand, 1, 8) / 10;
      a = randInt(rand, Math.round(b * 10) + 1, 9) / 10;
    } else if (difficulty === 2) {
      a = randInt(rand, 20, 90) / 10;
      b = randInt(rand, 10, Math.round(a * 10) - 1) / 10;
    } else {
      a = randInt(rand, 30, 99) / 10;
      b = randInt(rand, 10, Math.round(a * 10) - 1) / 10;
    }
    const diff = Math.round((a - b) * 10) / 10;
    correct = fmt(diff);
    prompt = `${fmt(a)} − ${fmt(b)} = ?`;

    const w1 = fmt(diff + 0.1);
    const w2 = fmt(diff > 0.1 ? diff - 0.1 : diff + 0.2);
    const w3 = fmt(diff + 1.0);

    options = [correct, w1, w2, w3];
    explanation = `小數減法：個位和小數位分別相減（需要時退位）。${fmt(a)} − ${fmt(b)} = ${correct}`;

  } else {
    // compare: which decimal is larger
    let a: number, b: number;
    if (difficulty === 1) {
      a = randInt(rand, 1, 9) / 10;
      do { b = randInt(rand, 1, 9) / 10; } while (b === a);
    } else {
      a = randInt(rand, 10, 99) / 10;
      do { b = randInt(rand, 10, 99) / 10; } while (b === a);
    }
    const bigger = a > b ? fmt(a) : fmt(b);
    const smaller = a < b ? fmt(a) : fmt(b);

    prompt = `${fmt(a)} 和 ${fmt(b)}，哪個比較大？`;
    correct = bigger;
    options = [bigger, smaller, fmt((a + b) / 2 * 10 === Math.round((a + b) / 2 * 10) ? (a + b) / 2 : a + 0.5), `兩個一樣大`];
    explanation = `比較小數：先比個位，再比小數位。${fmt(a)} ${a > b ? '>' : '<'} ${fmt(b)}，所以 ${bigger} 比較大`;
  }

  // Ensure 4 unique options
  const seen = new Set<string>();
  const uniqueOptions: string[] = [];
  for (const o of options) {
    if (!seen.has(o)) { seen.add(o); uniqueOptions.push(o); }
  }
  while (uniqueOptions.length < 4) {
    const extra = fmt(randInt(rand, 1, 99) / 10);
    if (!seen.has(extra) && extra !== correct) { seen.add(extra); uniqueOptions.push(extra); }
    else uniqueOptions.push(fmt(randInt(rand, 100, 199) / 10));
  }

  // Deterministic shuffle
  const shuffled = uniqueOptions.slice(0, 4).sort((x, y) => {
    const hx = x.split('').reduce((a, c) => a * 31 + c.charCodeAt(0), seed);
    const hy = y.split('').reduce((a, c) => a * 31 + c.charCodeAt(0), seed);
    return mulberry32(hx)() - mulberry32(hy)();
  });

  return {
    kind: 'mcq',
    prompt,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation,
    kp: 'math.decimal.addsub',
  };
}
